import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Ban, Unlock, Loader2, UserX } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { getImageUrl } from '../utils/imageUtils';

export default function BlockedUsersList() {
  const { api } = useAuth();
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unblocking, setUnblocking] = useState(null);

  useEffect(() => {
    fetchBlockedUsers();
  }, []);

  const fetchBlockedUsers = async () => {
    try {
      const response = await api.get('/users/blocked');
      setBlockedUsers(response.data.blocked_users || []);
    } catch (error) {
      console.error('Error fetching blocked users:', error);
      toast.error('Erreur lors du chargement des profils bloqués');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async (userId, pseudo) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir débloquer ${pseudo} ?`)) {
      return;
    }

    setUnblocking(userId);
    try {
      await api.delete(`/users/${userId}/block`);
      setBlockedUsers(prev => prev.filter(b => b.user?.id !== userId));
      toast.success(`${pseudo} a été débloqué`);
    } catch (error) {
      toast.error('Erreur lors du déblocage');
    } finally {
      setUnblocking(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  if (blockedUsers.length === 0) {
    return (
      <div className="text-center py-12">
        <Ban className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400">Aucun profil bloqué</p>
        <p className="text-gray-500 text-sm mt-1">
          Les profils que vous bloquez apparaîtront ici
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-serif text-lg text-white flex items-center gap-2">
          <Ban className="w-5 h-5 text-red-400" />
          Profils bloqués ({blockedUsers.length})
        </h3>
      </div>

      <div className="space-y-3">
        {blockedUsers.map((item) => {
          const user = item.user;
          const profile = item.profile;
          
          if (!user) return null;

          return (
            <div 
              key={user.id}
              className="glass-card p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                {/* Avatar */}
                <div className="w-12 h-12 rounded-full overflow-hidden bg-dark-subtle flex-shrink-0">
                  {profile?.profile_picture ? (
                    <img
                      src={getImageUrl(profile.profile_picture)}
                      alt={user.pseudo}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <UserX className="w-6 h-6 text-gray-500" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div>
                  <p className="font-medium text-white">{user.pseudo}</p>
                  <p className="text-sm text-gray-500">
                    Bloqué le {new Date(item.blocked_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <Button
                onClick={() => handleUnblock(user.id, user.pseudo)}
                disabled={unblocking === user.id}
                variant="outline"
                size="sm"
                className="border-white/20 hover:bg-white/5 text-gray-300"
                data-testid={`unblock-${user.id}`}
              >
                {unblocking === user.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Unlock className="w-4 h-4 mr-2" />
                    Débloquer
                  </>
                )}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
